/**
 * SaludApiClient -- el tablero de salud del sistema (Sala de Maquinas).
 *
 * MISION SALUD-TABLERO-V1-001, tarea SALUD-UI-T2.
 *
 * Fuente: GET /api/v1/salud/tablero, publicado por [SALUD-API-T1]. El contrato
 * lo fijo Ares y esta vista se construyo CONTRA EL, asi que las claves de aqui
 * son las de ese contrato: cambiarlas de un lado rompe el otro.
 *
 * FAIL-OPEN (patron PANEL-AUDIOS/PRESU): mientras el endpoint no exista, esto
 * devuelve un motivo legible en vez de romperse, y la vista lo dice en pantalla.
 * El dia que la API publique, se enciende sola SIN re-deploy del panel.
 * Verificado el 31-ago-2026: el endpoint responde 404 (control: una ruta
 * conocida responde 401 y la misma forma inventada 404).
 *
 * Al LEER se toleran las dos formas (camelCase del DTO y snake_case de la vista
 * de BD). Cuesta una linea y protege: la lectura perdona, la escritura no.
 *
 * @module infrastructure/api/SaludApiClient
 */

import { ApiBase } from './ApiBase.js';

const ENDPOINT = '/api/v1/salud/tablero';
// Los seis numeros del trabajo (IND-VIVO-T1 de Argy-A). Va aparte del
// tablero a proposito: son dos preguntas distintas y una puede estar
// publicada sin la otra.
const ENDPOINT_IND = '/api/v1/salud/indicadores';
// El cumplimiento de las Reglas v2 por Argy (PROTOCOLOS-V2-T2, nexo-api PR 278).
const ENDPOINT_CUMPL = '/api/v1/salud/cumplimiento-argys';
// Cada numero de arquitectura con su dueno y si esta vencido (SALA-ARQ-AUTO-T1, nexo-api PR 429).
const ENDPOINT_VIG = '/api/v1/salud/vigentes';
// Los chequeos versionados con su tendencia (SALUD-PANEL-T1, contrato A #32447).
const ENDPOINT_CHQ = '/api/v1/salud/chequeos';
// Las capas 0 y 1 de cada repo con su lista (Ares #38145; nexo-api PR 668).
const ENDPOINT_CAPAS = '/api/v1/salud/arquitectura-capas';
// La version del escritorio publicada, leida en el servidor (A #39229, nexo-api PR 694).
const ENDPOINT_ESCRITORIO = '/api/v1/salud/escritorio-version';

/** Primer valor no nulo entre varias claves posibles. */
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

export const SaludApiClient = {
  /**
   * Trae el tablero completo. Nunca lanza.
   * @returns {Promise<{ok:boolean, data?:object, motivo?:string, detalle?:string}>}
   */
  async getTablero() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      // El motivo importa: un mensaje unico para todo hace dudar de lo que si funciona.
      const txt = JSON.stringify(res.error || '');
      if (/not\s*found|404/i.test(txt)) {
        return { ok: false, motivo: 'NO_PUBLICADO', detalle: 'el endpoint del tablero todavia no esta publicado (SALUD-API-T1)' };
      }
      if (/401|unauthor/i.test(txt)) {
        return { ok: false, motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer el tablero' };
      }
      if (/403|forbidden/i.test(txt)) {
        return { ok: false, motivo: 'PROHIBIDO', detalle: 'esta vista es solo para el dueno del sistema' };
      }
      return { ok: false, motivo: 'ERROR_API', detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API' };
    }

    const d = res.data?.data ?? res.data ?? {};
    const arr = (x) => (Array.isArray(x) ? x : []);
    // SNAP-VIVO-T1 / SALA-ARQ-VISTA-T1: DE CUANDO ES cada dato. `generado` es la hora de la respuesta; la edad de la
    // arquitectura sale de aqui (la API lo mandaba y el panel no lo leia: "Medido hoy" sobre fotos de septiembre).
    const f = pick(d, 'foto', 'Foto') ?? {};

    return {
      ok: true,
      data: {
        generado: pick(d, 'generado', 'generated_at', 'generadoEn'),
        // SALUD-CLIENTES-T1 (#24401): el bloque viaja crudo; lo lee application/salud/clientes.js.
        clientes: pick(d, 'clientes', 'Clientes'),
        foto: {
          hoy: pick(f, 'hoy', 'Hoy'),
          arquitectura: pick(f, 'arquitectura', 'Arquitectura'),
          arquitecturaDiasAtras: num(pick(f, 'arquitecturaDiasAtras', 'ArquitecturaDiasAtras', 'arquitectura_dias_atras')),
          ultimaFoto: pick(f, 'ultimaFoto', 'UltimaFoto', 'ultima_foto'),
          ultimaFotoDiasAtras: num(pick(f, 'ultimaFotoDiasAtras', 'UltimaFotoDiasAtras', 'ultima_foto_dias_atras'))
        },
        agentes: arr(d.agentes).map((a) => ({
          agente: pick(a, 'agente', 'agent') ?? '(sin nombre)',
          entregas: num(pick(a, 'entregas')),
          arranques: num(pick(a, 'arranques')),
          assignsRecibidos: num(pick(a, 'assignsRecibidos', 'assigns_recibidos')),
          correcciones: num(pick(a, 'correcciones')),
          bloqueos: num(pick(a, 'bloqueos')),
          hallazgos: num(pick(a, 'hallazgos')),
          autonomiaPct: num(pick(a, 'autonomiaPct', 'autonomia_pct')),
          evidenciaPct: num(pick(a, 'evidenciaPct', 'evidencia_pct')),
          retrabajoPct: num(pick(a, 'retrabajoPct', 'retrabajo_pct'))
        })),
        cola: arr(d.cola).map((c) => ({
          agente: pick(c, 'agente', 'agent') ?? '(sin nombre)',
          pendientes: num(pick(c, 'pendientes')),
          masDe30d: num(pick(c, 'masDe30d', 'mas_de_30d')),
          zombisConDone: num(pick(c, 'zombisConDone', 'zombis_con_done')),
          zombisPct: num(pick(c, 'zombisPct', 'zombis_pct')),
          edadMedianaDias: num(pick(c, 'edadMedianaDias', 'edad_mediana_dias'))
        })),
        arquitectura: arr(d.arquitectura).map((x) => ({
          clave: pick(x, 'clave', 'key') ?? '',
          valor: num(pick(x, 'valor', 'value')),
          unidad: pick(x, 'unidad', 'unit') ?? '',
          direccionMeta: pick(x, 'direccionMeta', 'direccion_meta') ?? '',
          detalle: pick(x, 'detalle', 'detail') ?? null
        })),
        tendencia: arr(d.tendencia).map((t) => ({
          fecha: String(pick(t, 'fecha', 'date') ?? '').slice(0, 10),
          ambito: pick(t, 'ambito', 'scope') ?? '',
          clave: pick(t, 'clave', 'key') ?? '',
          sujeto: pick(t, 'sujeto', 'subject') ?? '',
          valor: num(pick(t, 'valor', 'value'))
        }))
      }
    };  },

  /**
   * LOS SEIS NUMEROS DEL TRABAJO (IND-VIVO-T2). Fuente: GET
   * /api/v1/salud/indicadores, publicado por Argy-A en [IND-VIVO-T1].
   *
   * MISMO FAIL-OPEN QUE getTablero, y por la misma razon: mientras el endpoint
   * no exista esto devuelve un motivo legible en vez de romperse, y el dia que
   * la API publique la seccion se enciende sola SIN re-deploy del panel.
   *
   * Se leen las dos formas (camelCase del DTO y la cruda de la funcion) porque
   * cuesta una linea: la lectura perdona, la escritura no.
   */
  async getIndicadores() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_IND);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      const txt = JSON.stringify(res.error || '');
      if (/not\s*found|404/i.test(txt)) {
        return { ok: false, motivo: 'NO_PUBLICADO', detalle: 'el endpoint de los seis numeros todavia no esta publicado (IND-VIVO-T1)' };
      }
      if (/401|unauthor/i.test(txt)) {
        return { ok: false, motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer los indicadores' };
      }
      if (/403|forbidden/i.test(txt)) {
        return { ok: false, motivo: 'PROHIBIDO', detalle: 'esta seccion es solo para el dueno del sistema' };
      }
      return { ok: false, motivo: 'ERROR_API', detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API' };
    }

    const d = res.data?.data ?? res.data ?? {};
    const crudos = pick(d, 'indicadores', 'Indicadores') ?? {};
    const explica = pick(d, 'explica', 'Explica') ?? {};
    const fuentes = pick(d, 'fuentes', 'Fuentes') ?? {};

    // EL NUMERO DEL INDICADOR VIVE PEGADO A SU CLAVE ('1_nos_conocemos') y es el
    // ORDEN en que el dueno los lee. Se separa AQUI, en el unico sitio que
    // conoce el contrato, y no en la vista.
    //
    // La lista sale de las claves que llegan, no de una escrita a mano: el dia
    // que la casa tenga un septimo indicador, aparece solo.
    const indicadores = Object.keys(crudos)
      .filter((k) => /^\d+_/.test(k))
      .sort()
      .map((k) => {
        const cuerpo = crudos[k] ?? {};
        const corte = k.indexOf('_');
        const clave = k.slice(corte + 1);
        const numeros = {};
        for (const [kk, vv] of Object.entries(cuerpo)) if (kk !== 'meta') numeros[kk] = vv;
        const valores = Object.values(numeros);
        return {
          orden: Number(k.slice(0, corte)),
          clave,
          meta: cuerpo.meta ?? null,
          numeros,
          // SIN DATOS NO ES CERO. El embudo llega vacio hasta que se toma la
          // foto; pintar 0 diria "no hay nada trabado", que es lo contrario.
          sinDatos: valores.length > 0 && valores.every((v) => v === null || v === undefined),
          explica: explica[k] ?? null,
          // null significa "no me la mandaron", NO "no tiene".
          fuente: fuentes[k] ?? fuentes[clave] ?? null
        };
      });

    return {
      ok: true,
      data: {
        corte: pick(d, 'corte', 'Corte'),
        fechaFoto: pick(d, 'fechaFoto', 'FechaFoto', 'fecha_foto'),
        fotoDiasAtras: num(pick(d, 'fotoDiasAtras', 'FotoDiasAtras', 'foto_dias_atras')),
        indicadores,
        traeFuentes: indicadores.some((i) => i.fuente !== null)
      }
    };
  },

  /**
   * EL CUMPLIMIENTO DE LAS REGLAS V2 POR ARGY (PROTOCOLOS-V2-T2 parte B). Fuente:
   * GET /api/v1/salud/cumplimiento-argys (nexo-api PR 278), que sirve
   * nexo_private.v_cumplimiento_argys.
   *
   * EL MOTIVO SALE DE res.code, NO DEL TEXTO. ApiBase traduce el error a una
   * frase en espanol ("Eso no existe...") y ya no dice 404: leerlo por texto
   * manda un endpoint sin publicar a ERROR_API (medido en saldos, PR 321).
   *
   * Devuelve las filas crudas; las lee application/salud/cumplimiento.js.
   */
  async getCumplimientoArgys() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_CUMPL);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      const codigo = Number(res.code ?? res.details?.status);
      if (codigo === 404) {
        return { ok: false, motivo: 'NO_PUBLICADO', detalle: 'el endpoint del cumplimiento todavia no esta publicado (PROTOCOLOS-V2-T2 parte A)' };
      }
      if (codigo === 401) {
        return { ok: false, motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer el cumplimiento' };
      }
      if (codigo === 403) {
        return { ok: false, motivo: 'PROHIBIDO', detalle: 'esta seccion es solo para el dueno del sistema' };
      }
      return { ok: false, motivo: 'ERROR_API', detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API' };
    }

    const d = res.data?.data ?? res.data;
    const filas = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []);
    return { ok: true, data: filas };
  },

  /**
   * CADA NUMERO DE ARQUITECTURA CON SU DUENO (SALA-ARQ-VISTA-T1 partes 1 y 2). Fuente:
   * GET /api/v1/salud/vigentes?ambito=arquitectura (nexo-api PR 429), que sirve
   * nexo_private.v_salud_vigente. Mismo motivo por res.code que getCumplimientoArgys.
   *
   * Devuelve las filas crudas; las lee application/salud/vigentes.js.
   */
  /**
   * Los chequeos versionados del sistema (SALUD-PANEL-T1; contrato P #32446, A #32447): ultima corrida y las 24
   * anteriores por chequeo, con nombre y frase del catalogo de la base. Nunca lanza; data = la respuesta tal cual.
   * @returns {Promise<{ok:boolean, data?:object, motivo?:string}>}
   */
  async getChequeos() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_CHQ);
    } catch {
      return { ok: false, motivo: 'SIN_RED' };
    }
    if (!res.ok) {
      const codigo = Number(res.code ?? res.details?.status);
      if (codigo === 404) return { ok: false, motivo: 'NO_PUBLICADO' };
      if (codigo === 401) return { ok: false, motivo: 'SIN_SESION' };
      if (codigo === 403) return { ok: false, motivo: 'PROHIBIDO' };
      return { ok: false, motivo: 'ERROR_API' };
    }
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async getVigentes(ambito = 'arquitectura') {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_VIG + '?ambito=' + encodeURIComponent(ambito));
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      const codigo = Number(res.code ?? res.details?.status);
      if (codigo === 404) {
        return { ok: false, motivo: 'NO_PUBLICADO', detalle: 'el endpoint de los numeros con dueno todavia no esta publicado (nexo-api PR 429)' };
      }
      if (codigo === 401) {
        return { ok: false, motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer los numeros de arquitectura' };
      }
      if (codigo === 403) {
        return { ok: false, motivo: 'PROHIBIDO', detalle: 'esta seccion es solo para el dueno del sistema' };
      }
      return { ok: false, motivo: 'ERROR_API', detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API' };
    }

    const d = res.data?.data ?? res.data;
    const filas = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []);
    return { ok: true, data: filas };
  },

  /**
   * Capas 0 y 1 por repo con su lista (Josse "va con las capas", Ares #38145; contrato A #38194, nexo-api PR 668):
   * la ultima foto de cada repo, [{repo, fecha, sha, medido_at, fuente, dueno, capas: {clave: {valor, lista, como, motivo}}}].
   * Nunca lanza; los lee application/salud/capasArquitectura.js.
   * @returns {Promise<{ok:boolean, data?:object[], motivo?:string}>}
   */
  /**
   * La version del escritorio que se esta publicando a los clientes (PROYECTOS-ALINEADOS-T1; contrato A #39229, nexo-api
   * PR 694): la API lee el manifiesto en el servidor porque el navegador no puede (sin CORS). 200 {version, url, leido_at,
   * desde_cache}; 502 ESCRITORIO_MANIFIESTO si no se pudo leer. Nunca lanza: si no esta publicado la vista dice pendiente.
   * @returns {Promise<{ok:boolean, data?:{version:string, leidoAt:string, desdeCache:boolean}, motivo?:string}>}
   */
  async getEscritorioVersion() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_ESCRITORIO);
    } catch {
      return { ok: false, motivo: 'SIN_RED' };
    }
    if (!res.ok) {
      const codigo = Number(res.code ?? res.details?.status);
      return { ok: false, motivo: codigo === 404 ? 'NO_PUBLICADO' : codigo === 502 ? 'MANIFIESTO' : 'ERROR_API' };
    }
    const d = res.data?.data ?? res.data ?? {};
    const version = pick(d, 'version', 'Version');
    if (!version) return { ok: false, motivo: 'SIN_VERSION' };
    return { ok: true, data: { version: String(version), leidoAt: String(pick(d, 'leido_at', 'leidoAt', 'LeidoAt') || ''), desdeCache: pick(d, 'desde_cache', 'desdeCache') === true } };
  },

  async getCapas() {
    let res;
    try {
      res = await ApiBase.get(ENDPOINT_CAPAS);
    } catch {
      return { ok: false, motivo: 'SIN_RED' };
    }
    if (!res.ok) {
      const codigo = Number(res.code ?? res.details?.status);
      if (codigo === 404) return { ok: false, motivo: 'NO_PUBLICADO' };
      if (codigo === 401) return { ok: false, motivo: 'SIN_SESION' };
      if (codigo === 403) return { ok: false, motivo: 'PROHIBIDO' };
      return { ok: false, motivo: 'ERROR_API' };
    }
    const d = res.data?.data ?? res.data;
    return { ok: true, data: Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []) };
  }
};
